import { toast } from "@/components/ui";
import { captureError } from "@/lib/monitoring";
import { useCallback, useEffect, useState } from "react";
import { RecorderErrors, useReactMediaRecorder } from "react-media-recorder";
import { useTranscribeAudio } from "../network/useTranscribeAudio";

interface UseVoiceRecordingProps {
  encounterId: string | undefined;
  onTranscriptionComplete: (text: string) => void;
  /**
   * Optional callback invoked when recording starts.
   * Use this for tracking, analytics, or other side effects.
   */
  onRecordingStart?: () => void;
  /**
   * Optional callback invoked when recording ends.
   * Use this for tracking, analytics, or other side effects.
   */
  onRecordingEnd?: () => void;
}

export const useVoiceRecording = ({
  encounterId,
  onTranscriptionComplete,
  onRecordingStart,
  onRecordingEnd,
}: UseVoiceRecordingProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const { mutate: transcribeAudio, isPending: isTranscribing } =
    useTranscribeAudio();

  const handleStop = useCallback(
    async (blobUrl: string) => {
      try {
        // Fetch the blob from the blob URL
        const audioBlob = await fetch(blobUrl).then((r) => r.blob());

        // Revoke the blob URL immediately after fetching the blob
        // The blob is now in memory, so we don't need the URL anymore
        // This prevents memory leaks while ensuring the blob data is already available
        URL.revokeObjectURL(blobUrl);

        // Call transcription API with the blob (which is now in memory, independent of the URL)
        // encounterId can be undefined for async chat without an encounter
        transcribeAudio(
          {
            encounterId,
            audioBlob,
          },
          {
            onSuccess: (response) => {
              if (response.text) {
                onTranscriptionComplete(response.text);
              }
            },
            onError: () => {
              toast.error(
                "Failed to transcribe audio. Please try recording again.",
              );
            },
          },
        );
      } catch (error) {
        // Clean up the blob URL if an error occurs before we could revoke it
        URL.revokeObjectURL(blobUrl);
        captureError(
          error instanceof Error
            ? error
            : new Error("Failed to process audio recording"),
        );
        toast.error(
          "Failed to process audio recording. Please try recording again.",
        );
      }
    },
    [encounterId, transcribeAudio, onTranscriptionComplete],
  );

  const { startRecording, stopRecording, status, error } =
    useReactMediaRecorder({
      audio: true,
      onStop: handleStop,
    });

  // Handle recording errors using RecorderErrors enum
  useEffect(() => {
    if (!error) {
      return;
    }

    // Handle permission denied errors
    if (
      error === RecorderErrors.NotAllowedError ||
      status === "permission_denied"
    ) {
      setPermissionDenied(true);
      setIsRecording(false);
      return;
    }

    // Handle other errors gracefully
    setIsRecording(false);

    const errorMessages: Record<string, string> = {
      [RecorderErrors.NotReadableError]:
        "Microphone is already in use by another application.",
      [RecorderErrors.NotFoundError]: "No microphone found on this device.",
      [RecorderErrors.AbortError]: "Recording was aborted.",
      [RecorderErrors.NO_RECORDER]: "Recorder error occurred.",
    };

    const errorMessage =
      errorMessages[error] || "An unknown recording error occurred.";
    toast.error(errorMessage);
  }, [error, status]);

  // Update isRecording state based on status
  useEffect(() => {
    setIsRecording(status === "recording");
  }, [status]);

  const handleStartRecording = useCallback(() => {
    // Reset permission denied state when attempting to start recording
    setPermissionDenied(false);
    startRecording();
    onRecordingStart?.();
  }, [startRecording, onRecordingStart]);

  const handleStopRecording = useCallback(() => {
    // Only fire tracking event if we were actually recording
    if (isRecording) {
      onRecordingEnd?.();
    }
    stopRecording();
  }, [stopRecording, onRecordingEnd, isRecording]);

  return {
    isRecording,
    isTranscribing,
    permissionDenied,
    handleStartRecording,
    handleStopRecording,
  };
};
